import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "devextreme-react/button";
import List from "devextreme-react/list";
import { Popup, Position } from "devextreme-react/popup";
import { ProgressBar } from "devextreme-react/progress-bar";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { run, sleep, useScope } from "effection";
import { Case, CaseElse, Switch } from "react-context-switch";
import { useTranslation } from "react-i18next";
import Balancer from "react-wrap-balancer";

import { fxDispatch, useFxSelector } from "@app/statefx";
import { isErr, isOk } from "@app/statefx/helpers";
import { schema } from "@app/statefx/schema";
import { sfxGetSpvMessages } from "@app/statefx/xThunks/ds-spv";
import closeCircle from "@iconify/icons-line-md/close-circle";
import downloadingLoop from "@iconify/icons-line-md/cloud-download-outline-loop";
import confirmCircle from "@iconify/icons-line-md/confirm-circle";
import { Icon } from "@iconify/react/dist/iconify.js";

import "./invoices.scss";

//<Icon icon="line-md:cloud-download-outline-loop"  style={{color: #8d6d9c}} />
import type { TExecution } from "@app/types/insider";

const NOT_STARTED_ITEM_STATUS = "loading";

const _ItemTemplate = ({ data }: { data: TExecution }) => {
  const value = data.result
    ? isOk(data.result)
      ? data.result.value
      : isErr(data.result)
      ? data.result.error.message || (data.result.error as unknown as string)
      : "-"
    : ""; // null - not started yet

  const filename = data.filename;
  return (
    <div key={data.id} style={{ borderBottom: "1px dotted #e4e4e4" }}>
      <Switch value={data}>
        <Case when={[(x) => x?.result && isOk(x.result)]}>
          <Switch value={value}>
            <Case when={["done"]}>
              <Icon icon={confirmCircle} style={{ color: "#4caf50" }} />
            </Case>
            <CaseElse>
              <Icon icon={downloadingLoop} style={{ color: "#4caf50" }} />
            </CaseElse>
          </Switch>
        </Case>
        <Case when={[(x) => x?.result && isErr(x.result)]}>
          <Icon icon={closeCircle} style={{ color: "#d75b5b" }} />
        </Case>
      </Switch>
      <span style={{ marginLeft: "10px" }}>{filename}</span>
      <Switch value={Boolean(value)}>
        <Case when={true}>
          <span style={{ marginRight: "10px", float: "right" }}>
            {"Rezultat: "}
            {value}
          </span>
        </Case>
        <CaseElse>
          <span style={{ marginRight: "10px", float: "right" }}>
            {"in asteptare"}
          </span>
        </CaseElse>
      </Switch>
    </div>
  );
};

const ItemTemplate = React.memo(_ItemTemplate);

export const SpvPopup = () => {
  const { t } = useTranslation();
  const { message } = useFxSelector(schema.notify.select);
  const prcList = useFxSelector(
    schema.execution.selectTableAsList
  ) as TExecution[];
  const [isVisible, setIsVisible] = useState(undefined);
  const [isDone, setIsDone] = useState(false);
  const lock = useFxSelector(schema.lock.select);
  const lDataSource = new DataSource({
    store: new CustomStore({
      loadMode: "raw",
      key: "id",
      load: function () {
        return prcList;
      },
      cacheRawData: true,
    }),
    paginate: false,
  });
  // (!)
  const isInProgress = useMemo(() => {
    return typeof lock === "string" && lock.startsWith("$:build");
  }, [lock]);

  const togglePopup = useCallback(() => {
    setIsVisible(!isVisible);
  }, [isVisible]);

  const onHidden = useCallback(() => {
    setIsVisible(undefined);
  }, []);
  const reloadDataset = useCallback(() => {
    fxDispatch(sfxGetSpvMessages());
    togglePopup();
  }, [isVisible]);

  const maxValue = prcList.length;
  const processed = prcList.filter((prc) => Boolean(prc.result)).length;
  const elementAttr = {
    class: "progress-bar-status",
  };
  const statusFormat = (value: number) => {
    return `${processed} / ${maxValue}`;
  };

  function* closeIfEmptyIn(msec: number) {
    yield* sleep(msec);
    if (isInProgress) {
      yield* closeIfEmptyIn(msec);
    } else {
      if (prcList.length === 0) {
        setIsVisible(false);
      }
    }
  }
  useEffect(() => {
    if (isInProgress) {
      if (isVisible === undefined) {
        setIsDone(false);
        setIsVisible(true);
      }
    } else {
      if (isVisible === true) {
        // is finished //
        setIsDone(true);
        run(() => closeIfEmptyIn(3_000));
      }
    }
  }, [isInProgress]);

  return (
    <div className="App">
      <Popup
        visible={isVisible}
        // visible={!isVisible} //--debug
        showTitle={true}
        title={t("download_and_parse")}
        hideOnOutsideClick={true}
        onHiding={togglePopup}
        onHidden={onHidden}
        shading={false}
        width={510}
        height={315}
        showCloseButton={false}
        resizeEnabled={false}
        dragEnabled={true}
        position="right bottom"
        className="dx-swatch-catetory-chooser-compact"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            padding: "0px",
            // background: '#cacaca',
          }}
        >
          <div className={"wda-vld-summ"}>
            <ProgressBar
              id="progress-bar-status"
              className={processed === maxValue ? "complete" : ""}
              width="90%"
              min={0}
              max={maxValue}
              elementAttr={elementAttr}
              statusFormat={statusFormat}
              value={processed}
            />
            <p style={{ fontWeight: "bold" }}>
              <Balancer>{isDone ? "Operatiune finalizata." : message}</Balancer>
            </p>
            <List
              style={{ backgroud: "#cacaca" }}
              key={"id"}
              className="dx-swatch-catetory-chooser-compact"
              dataSource={lDataSource}
              height={160}
              itemRender={(t) => <ItemTemplate data={t} />}
              showScrollbar="always"
              repaintChangesOnly={true}
            />
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: isDone ? "space-between" : "center",
              alignItems: "center",
              marginTop: "auto",
              width: "100%",
            }}
          >
            <Button
              className="dx-swatch-catetory-chooser-compact"
              text={isDone ? "Inchideti" : "Ascundeti"}
              stylingMode={"text"}
              icon={isDone ? "clear" : "eyeclose"}
              onClick={togglePopup}
            />

            <Button
              className="dx-swatch-catetory-chooser-compact"
              text={"Reincarcati lista"}
              stylingMode={"outlined"}
              type="success"
              icon={"refresh"}
              onClick={reloadDataset}
              visible={isDone}
            />
          </div>
        </div>

        <Position my="center" at="center" of="#anchorMain" />
      </Popup>
    </div>
  );
};
